import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Calendar,
  Clock,
  Video,
  User,
  Briefcase,
  Loader,
  Search,
  Filter,
  ChevronDown,
  ChevronUp,
  ArrowUpDown,
  X,
  Eye,
  Trash2,
  MoreVertical,
  CheckCircle,
  XCircle,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { mentorshipApi, showSuccess, showError, showInfo } from "../lib";
import { useAuth } from "../context/AuthContext";

interface Session {
  _id: string;
  mentor: {
    _id: string;
    user?: {
      name: string;
      firstName?: string;
      lastName?: string;
      avatar?: string;
    };
    specialty?: string;
    company?: string;
    expertise?: string[];
    rating?: number;
    reviewsCount?: number;
    avatar?: string;
  };
  student: {
    _id: string;
    name: string;
    firstName?: string;
    lastName?: string;
    avatar?: string;
  };
  scheduledAt: string;
  duration: number;
  status: "pending" | "approved" | "confirmed" | "completed" | "cancelled" | "no-show";
  price: number;
  topic: string;
  notes?: string;
  meetingLink?: string;
  createdAt: string;
}

const AdminMentorshipSessionsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [dateFilter, setDateFilter] = useState<{ start: string; end: string }>({
    start: "",
    end: "",
  });
  const [sortBy, setSortBy] = useState<
    "" | "date-asc" | "date-desc" | "price-asc" | "price-desc" | "status"
  >("");
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, pages: 1 });
  const [actionLoading, setActionLoading] = useState(false);

  const itemsPerPage = 20;

  useEffect(() => {
    if (!user) return;
    fetchSessions();
  }, [user, currentPage]);

  const fetchSessions = async () => {
    if (!user) return;
    try {
      setLoading(true);
      const result = await mentorshipApi.getAllSessions({
        page: currentPage,
        limit: itemsPerPage,
        status: statusFilter || undefined,
        search: searchQuery || undefined,
        sortBy: sortBy || undefined,
      });
      if (result.success) {
        setSessions(result.data || []);
        setPagination({
          total: result.pagination?.total || 0,
          pages: result.pagination?.pages || 1,
        });
      }
    } catch (err: any) {
      showError("Failed to load sessions");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSession = async (sessionId: string, topic: string) => {
    if (
      !window.confirm(
        `Are you sure you want to delete this session? This action cannot be undone.\n\nSession: "${topic || "Mentorship Session"}"`,
      )
    ) {
      return;
    }
    try {
      setActionLoading(true);
      const res = await mentorshipApi.deleteSession(sessionId);
      if (res.success) {
        showSuccess("Session deleted successfully");
        fetchSessions();
      }
    } catch (err: any) {
      showError(err.response?.data?.message || "Failed to delete session");
    } finally {
      setActionLoading(false);
    }
  };

  const formatDateTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return {
      date: date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      }),
      time: date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
      }),
    };
  };

  const getStatusConfig = (status: string) => {
    const configs: Record<string, { bg: string; text: string; label: string }> =
      {
        pending: {
          bg: "bg-amber-100",
          text: "text-amber-700",
          label: "Pending",
        },
        approved: {
          bg: "bg-emerald-100",
          text: "text-emerald-700",
          label: "Approved",
        },
        confirmed: {
          bg: "bg-blue-100",
          text: "text-blue-700",
          label: "Confirmed",
        },
        completed: {
          bg: "bg-blue-100",
          text: "text-blue-700",
          label: "Completed",
        },
        cancelled: {
          bg: "bg-red-100",
          text: "text-red-700",
          label: "Cancelled",
        },
        "no-show": {
          bg: "bg-gray-100",
          text: "text-gray-700",
          label: "No-Show",
        },
      };
    return configs[status] || configs.pending;
  };

  const fmtXAF = (amount: number) =>
    new Intl.NumberFormat("fr-CM", {
      style: "currency",
      currency: "XAF",
      maximumFractionDigits: 0,
    }).format(amount);

  const filteredAndSortedSessions = useMemo(() => {
    let result = [...sessions];

    if (statusFilter) {
      result = result.filter((s) => s.status === statusFilter);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter((s) => {
        const mentorName = s.mentor?.user?.name?.toLowerCase() || "";
        const studentName = s.student?.name?.toLowerCase() || "";
        const topic = (s.topic || "").toLowerCase();
        return (
          mentorName.includes(query) ||
          studentName.includes(query) ||
          topic.includes(query)
        );
      });
    }

    if (dateFilter.start || dateFilter.end) {
      result = result.filter((s) => {
        const sessionDate = new Date(s.scheduledAt).getTime();
        if (
          dateFilter.start &&
          sessionDate < new Date(dateFilter.start).setHours(0, 0, 0, 0)
        ) {
          return false;
        }
        if (
          dateFilter.end &&
          sessionDate > new Date(dateFilter.end).setHours(23, 59, 59, 999)
        ) {
          return false;
        }
        return true;
      });
    }

    if (sortBy) {
      result.sort((a, b) => {
        switch (sortBy) {
          case "date-asc":
            return (
              new Date(a.scheduledAt).getTime() -
              new Date(b.scheduledAt).getTime()
            );
          case "date-desc":
            return (
              new Date(b.scheduledAt).getTime() -
              new Date(a.scheduledAt).getTime()
            );
          case "price-asc":
            return a.price - b.price;
          case "price-desc":
            return b.price - a.price;
          case "status":
            const statusOrder = {
              pending: 0,
              approved: 1,
              confirmed: 2,
              completed: 3,
              cancelled: 4,
              "no-show": 5,
            };
            return (statusOrder[a.status] || 6) - (statusOrder[b.status] || 6);
          default:
            return 0;
        }
      });
    }

    return result;
  }, [sessions, statusFilter, searchQuery, dateFilter, sortBy]);

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {
      all: 0,
      pending: 0,
      approved: 0,
      confirmed: 0,
      completed: 0,
      cancelled: 0,
      "no-show": 0,
    };
    sessions.forEach((s) => {
      counts.all++;
      if (s.status in counts) counts[s.status]++;
    });
    return counts;
  }, [sessions]);

  const activeFilterCount =
    (statusFilter ? 1 : 0) +
    (searchQuery.trim() ? 1 : 0) +
    (dateFilter.start || dateFilter.end ? 1 : 0) +
    (sortBy ? 1 : 0);

  if (loading && currentPage === 1) {
    return (
      <div className="min-h-screen pt-16 flex justify-center items-center bg-gray-50">
        <Loader className="w-6 h-6 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <main className="min-h-screen pt-12 pb-12 bg-gray-50">
      <div className="px-3 mx-auto max-w-7xl">
        {/* Header */}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between py-4">
          <div>
            <h1 className="text-sm font-bold text-gray-900 sm:text-base">
              Mentorship Sessions
            </h1>
            <p className="text-[10px] text-gray-500">
              Manage all mentorship sessions
            </p>
          </div>
          <div className="text-[10px] text-gray-500">
            Total:{" "}
            <span className="font-semibold text-gray-900">
              {pagination.total}
            </span>{" "}
            sessions
          </div>
        </div>

        {/* Search and Controls */}
        <div className="space-y-2 mb-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
            <div className="relative flex-1 min-w-[140px]">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Search sessions..."
                className="w-full pl-8 pr-7 py-1.5 text-[11px] bg-white border border-gray-200 rounded-lg focus:border-gray-400 focus:ring-0 transition-colors"
              />
              {searchQuery && (
                             <button
                               onClick={() => navigate(`/dashboard/admin/sessions/${ session._id }`)}
                               className="p-1.5 text-gray-400 hover:text-primary hover:bg-gray-100 rounded" title="View details"
                             >
                               <Eye className="w-3.5 h-3.5" />
                             </button>
              )}
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setShowDateFilter(!showDateFilter)}
                className={`px-2.5 py-1.5 rounded-lg text-[11px] font-medium transition-all flex items-center gap-1 ${
                  dateFilter.start || dateFilter.end
                    ? "bg-blue-50 text-blue-700 border border-blue-200"
                    : "bg-white text-gray-600 border border-gray-200 hover:border-gray-300"
                }`}
              >
                <Filter className="w-3 h-3" />
                <span>Date</span>
                {(dateFilter.start || dateFilter.end) && (
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-600" />
                )}
              </button>

              <div className="relative">
                <button
                  onClick={() => {
                    setShowSortDropdown(!showSortDropdown);
                    setShowDateFilter(false);
                  }}
                  className={`px-2.5 py-1.5 rounded-lg text-[11px] font-medium transition-all flex items-center gap-1 ${
                    sortBy
                      ? "bg-gray-900 text-white border border-gray-900"
                      : "bg-white text-gray-600 border border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <ArrowUpDown className="w-3 h-3" />
                  <span>
                    {sortBy
                      ? sortBy === "date-asc"
                        ? "Date ↑"
                        : sortBy === "date-desc"
                          ? "Date ↓"
                          : "Sort"
                      : "Sort"}
                  </span>
                  {showSortDropdown ? (
                    <ChevronUp className="w-3 h-3" />
                  ) : (
                    <ChevronDown className="w-3 h-3" />
                  )}
                </button>
                {showSortDropdown && (
                  <div className="absolute top-full left-0 mt-1 py-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-[130px]">
                    {[
                      "",
                      "date-desc",
                      "date-asc",
                      "price-asc",
                      "price-desc",
                      "status",
                    ].map((opt) => (
                      <button
                        key={opt}
                        onClick={() => {
                          setSortBy(opt as any);
                          setShowSortDropdown(false);
                          setCurrentPage(1);
                        }}
                        className={`w-full px-3 py-1.5 text-left text-[11px] hover:bg-gray-50 ${
                          sortBy === opt
                            ? "bg-gray-50 text-gray-900 font-medium"
                            : "text-gray-600"
                        }`}
                      >
                        {opt === ""
                          ? "Clear sort"
                          : opt === "date-desc"
                            ? "Newest"
                            : opt === "date-asc"
                              ? "Oldest"
                              : opt === "price-asc"
                                ? "Price ↓"
                                : opt === "price-desc"
                                  ? "Price ↑"
                                  : "By Status"}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {activeFilterCount > 0 && (
                <button
                  onClick={() => {
                    setStatusFilter("");
                    setSearchQuery("");
                    setDateFilter({ start: "", end: "" });
                    setSortBy("");
                    setShowDateFilter(false);
                    setShowSortDropdown(false);
                    setCurrentPage(1);
                  }}
                  className="px-2.5 py-1.5 rounded-lg text-[11px] font-medium bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200"
                >
                  Clear ({activeFilterCount})
                </button>
              )}
            </div>
          </div>

          {showDateFilter && (
            <div className="p-2.5 bg-white border border-gray-200 rounded-lg shadow-sm flex flex-col sm:flex-row gap-2">
              <div className="flex-1">
                <label className="block text-[9px] font-medium text-gray-500 uppercase mb-0.5">
                  Start Date
                </label>
                <input
                  type="date"
                  value={dateFilter.start}
                  onChange={(e) =>
                    setDateFilter({ ...dateFilter, start: e.target.value })
                  }
                  className="w-full px-2 py-1.5 text-[11px] border border-gray-200 rounded-lg"
                />
              </div>
              <div className="flex-1">
                <label className="block text-[9px] font-medium text-gray-500 uppercase mb-0.5">
                  End Date
                </label>
                <input
                  type="date"
                  value={dateFilter.end}
                  onChange={(e) =>
                    setDateFilter({ ...dateFilter, end: e.target.value })
                  }
                  className="w-full px-2 py-1.5 text-[11px] border border-gray-200 rounded-lg"
                />
              </div>
              {(dateFilter.start || dateFilter.end) && (
                <button
                  onClick={() => setDateFilter({ start: "", end: "" })}
                  className="px-2 py-1.5 text-[10px] text-red-600 hover:bg-red-50 rounded-lg"
                >
                  Clear
                </button>
              )}
            </div>
          )}

          {/* Status Pills */}
          <div className="flex flex-wrap gap-1.5">
{[
               { key: "", label: "All", count: statusCounts.all },
               { key: "pending", label: "Pending", count: statusCounts.pending },
               { key: "approved", label: "Approved", count: statusCounts.approved },
               {
                 key: "confirmed",
                 label: "Confirmed",
                 count: statusCounts.confirmed,
               },
               {
                 key: "completed",
                 label: "Completed",
                 count: statusCounts.completed,
               },
               {
                 key: "cancelled",
                 label: "Cancelled",
                 count: statusCounts.cancelled,
               },
               {
                 key: "no-show",
                 label: "No-Show",
                 count: statusCounts["no-show"],
               },
             ].map((item) => (
              <button
                key={item.key || "all"}
                onClick={() => {
                  setStatusFilter(item.key);
                  setCurrentPage(1);
                }}
                className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-medium transition-all ${
                  statusFilter === item.key
                    ? "bg-gray-900 text-white shadow-sm"
                    : "bg-white text-gray-600 border border-gray-200 hover:border-gray-300"
                }`}
              >
                {item.label}
                <span
                  className={`px-1.5 py-0.5 rounded-full text-[8px] ${
                    statusFilter === item.key ? "bg-white/20" : "bg-gray-100"
                  }`}
                >
                  {item.count}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Sessions List */}
        {filteredAndSortedSessions.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
            <Calendar className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            <p className="text-[11px] text-gray-500">No sessions found</p>
          </div>
        ) : (
          <>
            {/* ── Mobile: card stack (< sm) ── */}
            <div className="sm:hidden space-y-2">
              {filteredAndSortedSessions.map((session) => {
                const { date, time } = formatDateTime(session.scheduledAt);
                const statusConfig = getStatusConfig(session.status);
                const studentName =
                  session.student?.name ||
                  `${session.student?.firstName || ""} ${session.student?.lastName || ""}`.trim() ||
                  "Unknown";
                const mentorName =
                  session.mentor?.user?.name ||
                  `${session.mentor?.user?.firstName || ""} ${session.mentor?.user?.lastName || ""}`.trim() ||
                  "Unknown Mentor";

                return (
                  <div
                    key={session._id}
                    className="bg-white rounded-xl border border-gray-200 p-3 shadow-sm"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                          {session.student?.avatar ? (
                            <img
                              src={session.student.avatar}
                              className="w-8 h-8 rounded-full object-cover"
                              alt=""
                            />
                          ) : (
                            <User className="w-3.5 h-3.5 text-primary" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[11px] font-semibold text-gray-900 truncate">
                            {studentName}
                          </p>
                          <p className="text-[9px] text-gray-400 truncate">
                            → {mentorName}
                          </p>
                        </div>
                      </div>
                      <span
                        className={`px-2 py-0.5 rounded-full text-[9px] font-medium whitespace-nowrap ${statusConfig.bg} ${statusConfig.text}`}
                      >
                        {statusConfig.label}
                      </span>
                    </div>

                    <p className="text-[11px] text-gray-700 font-medium mb-1.5 line-clamp-1">
                      {session.topic || "No topic"}
                    </p>

                    <div className="flex items-center gap-3 mb-2 text-[10px] text-gray-500">
                      <span className="flex items-center gap-0.5">
                        <Calendar className="w-2.5 h-2.5" />
                        {date}
                      </span>
                      <span className="flex items-center gap-0.5">
                        <Clock className="w-2.5 h-2.5" />
                        {time}
                      </span>
                    </div>

                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] bg-gray-100 px-1.5 py-0.5 rounded-full text-gray-600">
                        {session.duration} min
                      </span>
                      <span className="text-[11px] font-bold text-primary">
                        {fmtXAF(session.price)}
                      </span>
                    </div>

                    <div className="flex gap-1.5 pt-1.5 border-t border-gray-100">
                      <button
                        onClick={() =>
                          navigate(`/dashboard/admin/sessions/${session._id}`)
                        }
                        className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-[10px] font-medium text-white bg-primary rounded-lg hover:bg-primary/90 transition-colors"
                      >
                        <Eye className="w-3 h-3" /> View
                      </button>
                      <button
                        onClick={() =>
                          handleDeleteSession(session._id, session.topic)
                        }
                        disabled={actionLoading}
                        className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-[10px] font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                      >
                        <Trash2 className="w-2.5 h-2.5" /> Delete
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* ── Desktop: scrollable table (≥ sm) ── */}
            <div className="hidden sm:block bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[760px] border-collapse">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                        Student → Mentor
                      </th>
                      <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                        Topic
                      </th>
                      <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                        Date & Time
                      </th>
                      <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                        Duration
                      </th>
                      <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                        Status
                      </th>
                      <th className="text-right px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                        Price
                      </th>
                      <th className="text-right px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredAndSortedSessions.map((session) => {
                      const { date, time } = formatDateTime(
                        session.scheduledAt,
                      );
                      const statusConfig = getStatusConfig(session.status);
                      const studentName =
                        session.student?.name ||
                        `${session.student?.firstName || ""} ${session.student?.lastName || ""}`.trim() ||
                        "Unknown";
                      const mentorName =
                        session.mentor?.user?.name ||
                        `${session.mentor?.user?.firstName || ""} ${session.mentor?.user?.lastName || ""}`.trim() ||
                        "Unknown Mentor";

                      return (
                        <tr
                          key={session._id}
                          className="hover:bg-gray-50 transition-colors"
                        >
                          {/* Student → Mentor */}
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2 min-w-0">
                              <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                                {session.student?.avatar ? (
                                  <img
                                    src={session.student.avatar}
                                    className="w-7 h-7 rounded-full object-cover"
                                    alt=""
                                  />
                                ) : (
                                  <User className="w-3 h-3 text-primary" />
                                )}
                              </div>
                              <div className="min-w-0">
                                <p className="text-[11px] font-semibold text-gray-900 truncate max-w-[130px]">
                                  {studentName}
                                </p>
                                <p className="text-[9px] text-gray-400 truncate max-w-[130px]">
                                  → {mentorName}
                                </p>
                              </div>
                            </div>
                          </td>

                          {/* Topic */}
                          <td className="px-4 py-3 max-w-[180px]">
                            <p className="text-[11px] text-gray-700 font-medium truncate">
                              {session.topic || "No topic"}
                            </p>
                          </td>

                          {/* Date & Time */}
                          <td className="px-4 py-3 whitespace-nowrap">
                            <p className="text-[11px] text-gray-700">{date}</p>
                            <p className="text-[9px] text-gray-400">{time}</p>
                          </td>

                          {/* Duration */}
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="text-[10px] bg-gray-100 px-2 py-0.5 rounded-full text-gray-600">
                              {session.duration} min
                            </span>
                          </td>

                          {/* Status */}
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span
                              className={`px-2 py-0.5 rounded-full text-[9px] font-medium ${statusConfig.bg} ${statusConfig.text}`}
                            >
                              {statusConfig.label}
                            </span>
                          </td>

                          {/* Price */}
                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            <span className="text-[11px] font-bold text-primary">
                              {fmtXAF(session.price)}
                            </span>
                          </td>

                          {/* Actions */}
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-1.5">
                              <button
                                onClick={() =>
                                  navigate(
                                    `/dashboard/admin/sessions/${session._id}`,
                                  )
                                }
                                className="flex items-center gap-1 px-3 py-1.5 text-[10px] font-medium text-white bg-primary rounded-lg hover:bg-primary/90 transition-colors whitespace-nowrap"
                              >
                                <Eye className="w-3 h-3" /> View
                              </button>
                              <button
                                onClick={() =>
                                  handleDeleteSession(
                                    session._id,
                                    session.topic,
                                  )
                                }
                                disabled={actionLoading}
                                className="flex items-center gap-1 px-3 py-1.5 text-[10px] font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors whitespace-nowrap disabled:opacity-50"
                              >
                                <Trash2 className="w-3 h-3" /> Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="flex flex-col items-center gap-2 mt-4">
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1 || loading}
                    className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50"
                  >
                    <ChevronLeft className="w-3.5 h-3.5" />
                  </button>
                  <span className="text-[10px] text-gray-500 px-2">
                    Page {currentPage} of {pagination.pages}
                  </span>
                  <button
                    onClick={() =>
                      setCurrentPage(
                        Math.min(pagination.pages, currentPage + 1),
                      )
                    }
                    disabled={currentPage === pagination.pages || loading}
                    className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50"
                  >
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="text-[9px] text-gray-400">
                  Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                  {Math.min(currentPage * itemsPerPage, pagination.total)} of{" "}
                  {pagination.total}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </main>
  );
};

export default AdminMentorshipSessionsPage;
